import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { Box, Button, Chip, Slider, Stack, Typography } from '@mui/material';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { MessageKey, useT } from '../i18n';
import { useAdFree } from '../monetization/adFree';
import { useAds } from '../monetization/AdsProvider';
import { playCue } from '../tarot/feedback';

import { hasNativeSleep, nativeSleep } from './nativeSleep';
import { ensureSleepUnlocked } from './sleepGate';
import {
    createSleepPlayer,
    FADE_SECONDS,
    SLEEP_SOUNDS,
    SleepSound,
} from './soundEngine';
import {
    DEFAULT_TIMER_MINUTES,
    formatRemaining,
    minutesToStop,
    splitMinutes,
    stopToMinutes,
    TIMER_LAST_STOP,
    TIMER_SHORTCUTS,
} from './timerScale';
import { Visualizer } from './Visualizer';

const NAMES: Record<SleepSound, MessageKey> = {
    rain: 'sleep.rain',
    brown: 'sleep.brown',
    ocean: 'sleep.ocean',
};

/** Short landscape screens have no room to stack the controls under the portal, so they sit beside it. */
const SHORT_LANDSCAPE =
    '@media (orientation: landscape) and (max-height: 520px)';

const SLIDER_MARKS = TIMER_SHORTCUTS.map((m) => ({ value: minutesToStop(m) }));

/** Rain, brown noise and ocean, made on the device, with a sleep timer and a full-screen portal. */
export const SleepSoundsPage: React.FC = () => {
    const t = useT();
    const navigate = useNavigate();
    const adFree = useAdFree();
    const { requestUnlock } = useAds();
    const player = useMemo(() => createSleepPlayer(), []);
    // On Android the service plays (also in the background) and owns the timer; elsewhere Web Audio does.
    const native = useMemo(() => hasNativeSleep(), []);
    const [sound, setSound] = useState<SleepSound | null>(null);
    const [minutes, setMinutes] = useState<number>(DEFAULT_TIMER_MINUTES);
    const [remaining, setRemaining] = useState(0);
    const [busy, setBusy] = useState(false);
    const [declined, setDeclined] = useState(false);
    const deadline = useRef(0);
    const stage = useRef<HTMLDivElement>(null);

    const stop = useCallback(() => {
        if (native) void nativeSleep.stop().catch(() => undefined);
        else player.stop();
        setSound(null);
        setRemaining(0);
    }, [player, native]);

    useEffect(() => () => player.close(), [player]);

    // Native: the sound can end (timer, notification Stop) or still be playing while the page was away or hidden.
    useEffect(() => {
        if (!native) return;
        const sync = () =>
            nativeSleep
                .getState()
                .then((state) => {
                    if (state.sound && state.endsAt > Date.now()) {
                        deadline.current = state.endsAt;
                        setRemaining((state.endsAt - Date.now()) / 1000);
                        setSound(state.sound);
                    } else {
                        setSound(null);
                        setRemaining(0);
                    }
                })
                .catch(() => undefined);
        const onVisible = () => {
            if (document.visibilityState === 'visible') void sync();
        };
        void sync();
        document.addEventListener('visibilitychange', onVisible);
        const stopped = nativeSleep.addListener('stopped', () => {
            setSound(null);
            setRemaining(0);
        });
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            void stopped.then((h) => h.remove()).catch(() => undefined);
        };
    }, [native]);

    // The timer: fade out for the last seconds, then stop.
    useEffect(() => {
        if (!sound) return;
        let fading = false;
        const id = window.setInterval(() => {
            const left = (deadline.current - Date.now()) / 1000;
            setRemaining(left);
            // Native fades and stops by itself; this loop only keeps the countdown.
            if (!native && left <= FADE_SECONDS && !fading) {
                fading = true;
                player.fadeOut(Math.max(left, 1));
            }
            if (left <= 0) stop();
        }, 500);
        return () => window.clearInterval(id);
    }, [sound, player, stop, native]);

    const play = async (next: SleepSound) => {
        if (busy) return;
        setBusy(true);
        setDeclined(false);
        try {
            if (!(await ensureSleepUnlocked(adFree, requestUnlock))) {
                setDeclined(true);
                return;
            }
            if (native) {
                const { endsAt } = await nativeSleep.start({
                    sound: next,
                    minutes,
                    fadeSeconds: FADE_SECONDS,
                    title: t('sleep.title'),
                    text: t(NAMES[next]),
                    stopLabel: t('sleep.stop'),
                });
                deadline.current = endsAt;
            } else {
                player.start(next);
                deadline.current = Date.now() + minutes * 60_000;
            }
            setRemaining(minutes * 60);
            setSound(next);
        } finally {
            setBusy(false);
        }
    };

    const durationLabel = (total: number): string => {
        const split = splitMinutes(total);
        if (!split.hours) return t('sleep.minutes', { n: split.minutes });
        return split.minutes
            ? t('sleep.hoursMinutes', { h: split.hours, m: split.minutes })
            : t('sleep.hours', { n: split.hours });
    };

    // Native keeps playing with the screen off, so the web advice to keep the app open does not apply.
    const noteKey: MessageKey = native
        ? adFree
            ? 'sleep.noteBackgroundFree'
            : 'sleep.noteBackground'
        : 'sleep.note';

    return (
        <Box
            component="section"
            sx={{
                position: 'fixed',
                inset: 0,
                // Over the app bar and footer, under dialogs such as the rewarded-video prompt.
                zIndex: (theme) => theme.zIndex.appBar + 1,
                overflow: 'hidden',
                color: 'common.white',
                background:
                    'radial-gradient(circle at 50% 38%, #2a1458 0%, #0b0618 55%, #040209 100%)',
            }}
        >
            <Visualizer
                analyser={player.analyser}
                sound={sound}
                stage={stage}
            />
            <Box
                sx={{
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    // Android passes the system bars in as --inset-*: env() reads 0 in its edge-to-edge WebView. As in Shell.
                    pt: 'var(--inset-top, env(safe-area-inset-top, 0px))',
                    pl: 'var(--inset-left, env(safe-area-inset-left, 0px))',
                    pr: 'var(--inset-right, env(safe-area-inset-right, 0px))',
                    // The phone app's ad banner floats over the bottom of the WebView.
                    pb: 'calc(var(--inset-bottom, env(safe-area-inset-bottom, 0px)) + var(--banner-height, 0px))',
                    [SHORT_LANDSCAPE]: {
                        display: 'grid',
                        gridTemplateColumns: '1fr minmax(300px, 42%)',
                        gridTemplateRows: 'auto 1fr',
                    },
                }}
            >
                <Box
                    sx={{
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        [SHORT_LANDSCAPE]: { gridColumn: 1 },
                    }}
                >
                    <Button
                        color="inherit"
                        startIcon={<ArrowBackRoundedIcon />}
                        onClick={() => {
                            playCue('ui');
                            navigate('/');
                        }}
                    >
                        {t('app.back')}
                    </Button>
                    <Typography
                        component="h1"
                        variant="subtitle1"
                        sx={{ ml: 'auto', mr: 1, opacity: 0.85 }}
                    >
                        {t('sleep.title')}
                    </Typography>
                </Box>
                <Box
                    ref={stage}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        [SHORT_LANDSCAPE]: { gridColumn: 1, gridRow: 2 },
                    }}
                >
                    <Typography
                        data-testid="sleep-readout"
                        aria-live="off"
                        variant="h4"
                        component="p"
                        sx={{
                            fontVariantNumeric: 'tabular-nums',
                            // Has to fit inside the mouth of the portal on a phone.
                            fontSize: { xs: '1.35rem', sm: '2.125rem' },
                            [SHORT_LANDSCAPE]: { fontSize: '1.35rem' },
                            textShadow: '0 0 18px rgba(4,2,9,.9)',
                            px: 1,
                        }}
                    >
                        {sound
                            ? formatRemaining(remaining)
                            : durationLabel(minutes)}
                    </Typography>
                </Box>
                <Stack
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                        width: '100%',
                        maxWidth: 520,
                        mx: 'auto',
                        px: 2.5,
                        pb: 2,
                        maxHeight: '60%',
                        overflowY: 'auto',
                        [SHORT_LANDSCAPE]: {
                            gridColumn: 2,
                            gridRow: '1 / 3',
                            maxHeight: '100%',
                            alignSelf: 'center',
                            py: 1,
                        },
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                        sx={{ width: '100%' }}
                    >
                        {SLEEP_SOUNDS.map((s) => (
                            <Button
                                key={s}
                                variant={sound === s ? 'contained' : 'outlined'}
                                color={sound === s ? 'primary' : 'inherit'}
                                disabled={busy}
                                aria-pressed={sound === s}
                                onClick={() => void play(s)}
                                sx={{
                                    flex: '1 1 auto',
                                    px: 1.5,
                                    borderRadius: 999,
                                    whiteSpace: 'nowrap',
                                    fontSize: { xs: '0.8rem', sm: undefined },
                                    letterSpacing: {
                                        xs: '0.02em',
                                        sm: undefined,
                                    },
                                    [SHORT_LANDSCAPE]: {
                                        px: 1,
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.02em',
                                    },
                                    backdropFilter: 'blur(6px)',
                                }}
                            >
                                {t(NAMES[s])}
                            </Button>
                        ))}
                    </Stack>
                    {sound ? (
                        <>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                {t('sleep.remaining', {
                                    time: formatRemaining(remaining),
                                })}
                            </Typography>
                            <Button
                                color="inherit"
                                startIcon={<StopRoundedIcon />}
                                onClick={stop}
                            >
                                {t('sleep.stop')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Slider
                                aria-label={t('sleep.timer')}
                                aria-valuetext={durationLabel(minutes)}
                                min={0}
                                max={TIMER_LAST_STOP}
                                step={1}
                                marks={SLIDER_MARKS}
                                value={minutesToStop(minutes)}
                                valueLabelDisplay="off"
                                onChange={(_, stop) =>
                                    setMinutes(stopToMinutes(stop as number))
                                }
                                sx={{
                                    height: 10,
                                    py: 2,
                                    '& .MuiSlider-rail': {
                                        bgcolor: 'common.white',
                                        opacity: 0.22,
                                    },
                                    '& .MuiSlider-track': {
                                        border: 0,
                                        background:
                                            'linear-gradient(90deg, #5ad1ff, #b57bff 55%, #ff7bc0)',
                                    },
                                    '& .MuiSlider-mark': {
                                        bgcolor: 'common.white',
                                        opacity: 0.55,
                                        height: 4,
                                        width: 4,
                                        borderRadius: '50%',
                                    },
                                    '& .MuiSlider-thumb': {
                                        height: 30,
                                        width: 30,
                                        bgcolor: 'common.white',
                                        boxShadow:
                                            '0 0 16px 3px rgba(181,123,255,.75)',
                                        transition:
                                            'box-shadow .15s, width .15s, height .15s',
                                        '&:hover, &.Mui-focusVisible': {
                                            boxShadow:
                                                '0 0 22px 7px rgba(181,123,255,.8)',
                                        },
                                        '&.Mui-active': {
                                            height: 36,
                                            width: 36,
                                            boxShadow:
                                                '0 0 28px 10px rgba(255,123,192,.7)',
                                        },
                                    },
                                }}
                            />
                            <Stack
                                direction="row"
                                useFlexGap
                                flexWrap="wrap"
                                spacing={0.75}
                                justifyContent="center"
                                role="group"
                                aria-label={t('sleep.timer')}
                            >
                                {TIMER_SHORTCUTS.map((m) => (
                                    <Chip
                                        key={m}
                                        size="small"
                                        label={durationLabel(m)}
                                        variant={
                                            minutes === m
                                                ? 'filled'
                                                : 'outlined'
                                        }
                                        aria-pressed={minutes === m}
                                        onClick={() => setMinutes(m)}
                                        sx={{
                                            color: 'common.white',
                                            borderColor: 'rgba(255,255,255,.4)',
                                            ...(minutes === m && {
                                                bgcolor:
                                                    'rgba(255,255,255,.26)',
                                            }),
                                        }}
                                    />
                                ))}
                            </Stack>
                        </>
                    )}
                    {declined && (
                        <Typography
                            variant="body2"
                            role="status"
                            textAlign="center"
                        >
                            {t('sleep.declined')}
                        </Typography>
                    )}
                    <Typography
                        variant="caption"
                        textAlign="center"
                        sx={{ opacity: 0.7 }}
                    >
                        {sound ? t(noteKey) : t('sleep.intro')}
                    </Typography>
                </Stack>
            </Box>
        </Box>
    );
};
